import { Translate, useI18n } from '@change-corgi/core/react/i18n';
import { Button } from '@change-corgi/design-system-legacy/components/actions';
import { PageMessage } from '@change-corgi/design-system-legacy/components/alerts';
import { Card, Separator } from '@change-corgi/design-system-legacy/components/content';
import { Loader } from '@change-corgi/design-system-legacy/components/progressiveDisclosure';
import { Flex } from '@change-corgi/design-system-legacy/layout';
import { Heading, Text } from '@change-corgi/design-system-legacy/typography';

import { usePetitionDetailsPageUserData } from 'src/app/pages/petitionDetails/pageContext';

import { ShareVisualContent } from './components/ShareVisualContent';
import { useShareVisual } from './hooks/useShareVisual';

function ShareVisualLoading(): JSX.Element {
	return (
		<>
			<Separator role="presentation" my={24} />
			<Card
				p="24px"
				mt="16px"
				backgroundColor="white"
				data-qa="share-visual-container"
				data-testid="share-visual-loading-container"
				sx={{ border: 'unset', borderRadius: 'big' }}
			>
				<Loader role="progressbar" size="s" mb="20px" mx="auto" aria-label="progressbar" />
				<Text as="div" size="large" sx={{ textAlign: 'center' }}>
					<Translate value="corgi.petition_gamma.details.share_visuals.loading_copy" />
				</Text>
			</Card>
			<Separator role="presentation" my={24} />
		</>
	);
}

function ShareVisualTimeout(): JSX.Element {
	return (
		<>
			<Separator role="presentation" my={24} />
			<Card
				p="24px"
				mt="16px"
				backgroundColor="white"
				data-qa="share-visual-container"
				data-testid="share-visual-timeout-container"
				sx={{ border: 'unset', borderRadius: 'big' }}
			>
				<Text as="div" size="large" sx={{ textAlign: 'center' }}>
					<Translate value="corgi.petition_gamma.details.share_visuals.timeout_copy" />
				</Text>
			</Card>
			<Separator role="presentation" my={24} />
		</>
	);
}

export function ShareVisual(): JSX.Element | null {
	const {
		data: { buttonDisabled, petition, shareVisualEnabled, shareVisualsState, shareVisualDownloaded },
		actions,
	} = useShareVisual();
	const { translate } = useI18n();
	const {
		data: { decisionMaker, signed },
	} = usePetitionDetailsPageUserData();

	if (
		!shareVisualEnabled ||
		decisionMaker ||
		petition.status === 'CLOSED' ||
		petition.status === 'VICTORY' ||
		petition.shareVisuals.length === 0 ||
		!signed
	) {
		return null;
	}

	if (shareVisualsState === 'loading') return <ShareVisualLoading />;

	if (shareVisualsState === 'timeout') return <ShareVisualTimeout />;

	return (
		<>
			<Separator role="presentation" my={24} />
			<Card
				mb={['24px', '64px']}
				backgroundColor="white"
				data-qa="share-visual-container"
				data-testid="share-visual-loaded-container"
				sx={{ border: 'unset', borderRadius: 'big' }}
			>
				<Heading px="8px" as="h3" size="h3" id="share-petition-visuals-title" data-qa="share-petition-visuals-title">
					<Translate value="corgi.petition_gamma.details.share_visuals.title" />
				</Heading>
				<Text px="8px" as="p" mt={['16px', '24px']}>
					<Translate value="corgi.petition_gamma.details.share_visuals.description" />
				</Text>
				<form
					onSubmit={actions.onShareVisualDownload}
					sx={{ display: 'flex', flexDirection: 'column', marginTop: 24 }}
					data-qa="share-visual-modal-form"
				>
					<ShareVisualContent petition={petition} context="petition_page" onChange={actions.onShareVisualSelect} />
					<Flex sx={{ flexDirection: ['column', 'row', 'row'], gap: 16 }}>
						<Button
							variant="secondaryEmphasis"
							type="submit"
							disabled={buttonDisabled}
							data-qa="share-visual-modal-download"
						>
							<Text sx={{ display: ['none', 'block'] }} breakWord>
								<Translate value="corgi.petition_gamma.details.share_visuals.action_button" />
							</Text>
							<Text sx={{ display: ['block', 'none'] }} breakWord>
								<Translate value="corgi.petition_gamma.details.share_visuals.action_button_mobile" />
							</Text>
						</Button>
					</Flex>
				</form>
				{shareVisualDownloaded && (
					<>
						<PageMessage
							mt={24}
							message={translate('corgi.petition_gamma.details.share_visuals.downloaded_desktop')}
							severity="success"
							containerVariant="small"
							sx={{ borderRadius: '0.5rem', display: ['none', 'block'] }}
						/>
						<PageMessage
							mt={24}
							message={translate('corgi.petition_gamma.details.share_visuals.downloaded_mobile')}
							severity="success"
							containerVariant="small"
							sx={{ borderRadius: '0.5rem', display: ['block', 'none'] }}
						/>
					</>
				)}
			</Card>
			<Separator role="presentation" my={24} />
		</>
	);
}
